import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Alert,
} from "@mui/material";
import {
    Close as CloseIcon,
    Info as InfoIcon,
    Download as DownloadIcon,
    FilterList as FilterIcon,
    BarChart as ChartIcon,
    Help as HelpIcon,
} from "@mui/icons-material";

interface HelpModalProps {
    open: boolean;
    onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: "70vh" },
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <HelpIcon color="primary" />
                    <Typography variant="h6">Как пользоваться личным кабинетом</Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Личный кабинет предназначен для просмотра статистики использования вашего контента на
                            устройствах abox.
                        </Typography>
                    </Alert>
                </Box>

                <Typography variant="h6" gutterBottom>
                    Основные разделы
                </Typography>

                <List>
                    <ListItem>
                        <ListItemIcon>
                            <ChartIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Общая статистика"
                            secondary="Сводная информация по всем показателям за выбранный период"
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            <InfoIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Статистика страниц"
                            secondary="Анализ просмотров HTML-страниц и каталогов контента"
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            <InfoIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Статистика медиа"
                            secondary="Детальная аналитика по просмотрам видео-контента"
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            <InfoIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Статистика устройств"
                            secondary="Анализ использования контента по типам устройств abox"
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            <InfoIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Статистика рейсов"
                            secondary="Анализ просмотров контента во время полетов"
                        />
                    </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                    Фильтрация данных
                </Typography>

                <List>
                    <ListItem>
                        <ListItemIcon>
                            <FilterIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="По датам"
                            secondary="Используйте календарь для выбора периода. По умолчанию показываются данные за последние 30 дней"
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            <FilterIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="По клиентам"
                            secondary="Выберите конкретную авиакомпанию из списка или оставьте 'Все клиенты'"
                        />
                    </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                    Экспорт данных
                </Typography>

                <List>
                    <ListItem>
                        <ListItemIcon>
                            <DownloadIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Формат CSV"
                            secondary="Все разделы поддерживают экспорт в CSV с заголовками столбцов"
                        />
                    </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                    Понимание метрик
                </Typography>

                <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Просмотры:</strong> Количество обращений к странице или медиа-файлу
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Уникальные посетители:</strong> Количество уникальных IP-адресов
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Общий трафик:</strong> Объем данных, переданных при просмотре медиа-контента
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                    Часто задаваемые вопросы
                </Typography>

                <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Q:</strong> Почему суммы уникальных посетителей не совпадают между вкладками?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        <strong>A:</strong> Один пользователь может просматривать и страницы, и медиа, поэтому его IP
                        учитывается в обеих статистиках.
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Q:</strong> Что означают URL страниц?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        <strong>A:</strong> URL показывают структуру контента. Например,{" "}
                        <code>/series/vyzhivshie/</code> означает страницу сериала &quot;Выжившие&quot;.
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Q:</strong> Как часто обновляются данные?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        <strong>A:</strong> Данные обновляются в реальном времени. Используйте кнопку
                        &quot;Обновить&quot; для получения актуальной информации.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} startIcon={<CloseIcon />}>
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HelpModal;
